import { createStore } from "vuex";
import router from "@/router";
import createPersistedState from "vuex-persistedstate";
import { ProfileModel } from "@/models/profile";
import { GET_PROFILE_INFO } from "@/services/header-profile-info-service";
import type { SanitizedAuth0User } from "@/plugins/launchDarkly/utils";

import { returnErrors } from "@/utils/returnErrors";

import { notifyError } from "@/utils/notifier.js";

const loggedInKey = "vue_user_loggedIn";
const authenticatedKey = "vue_user_authenticated";

type Auth0AuthStatePayload = {
  accessToken: string;
  auth0User: SanitizedAuth0User;
  permissions: string[];
  userEmail: string;
  userName: string;
};

// @ts-ignore
export default createStore({
  plugins: [
    createPersistedState({
      paths: [
        "isLoggedIn",
        "isAuthenticated",
        "drawer",

        "roleGuid",
        "roleName",
        "userGuid",
        "ownerGuid",
        "ownerName",
        "ownerRole",
        "ownerId",
        "isReportReady",
        "is401",
        "token",
        "userVaultnPermissions",
        "allVaultnPermissions",
        "profileData",
        "auth0User",
      ],
    }),
  ],

  state: {
    isLoggedIn: false,
    loadingSpinner: false,
    profileLoading: false,
    profileData: new ProfileModel(),
    drawer: {
      open: true,
    },

    roleGuid: "",
    roleName: "",
    userGuid: "",
    ownerGuid: "",
    ownerName: "",
    ownerRole: "",
    userId: "",
    ownerId: "",
    isAuthenticated: false,
    isReportReady: false,
    is401: false,
    token: "",
    userVaultnPermissions: [] as string[],
    allVaultnPermissions: [] as string[],
    auth0User: null as SanitizedAuth0User | null,
  },
  mutations: {
    logIn(state) {
      state.isLoggedIn = true;
      sessionStorage.setItem(loggedInKey, state.isLoggedIn.toString());
      router.push("/2-fa-authenticate/");
    },
    logOut(state) {
      state.isLoggedIn = false;
      state.isAuthenticated = false;
      state.profileData = new ProfileModel();
      state.drawer.open = true;
      state.userVaultnPermissions = [];
      state.allVaultnPermissions = [];
      state.auth0User = null;
      sessionStorage.setItem(loggedInKey, state.isLoggedIn.toString());
      sessionStorage.setItem(
        authenticatedKey,
        state.isAuthenticated.toString(),
      );
      localStorage.clear();
      sessionStorage.clear();
    },
    clearLocalAuthState(state) {
      state.isLoggedIn = false;
      state.isAuthenticated = false;
      state.is401 = false;
      state.token = "";
      state.auth0User = null;
      state.profileData = new ProfileModel();
      state.userVaultnPermissions = [];
      state.allVaultnPermissions = [];
      state.roleGuid = "";
      state.roleName = "";
      state.userGuid = "";
      state.ownerGuid = "";
      state.ownerName = "";
      state.ownerRole = "";
      state.userId = "";
      state.ownerId = "";

      sessionStorage.setItem(loggedInKey, state.isLoggedIn.toString());
      sessionStorage.setItem(authenticatedKey, state.isAuthenticated.toString());
    },
    rebuildAuth0AuthState(state, payload: Auth0AuthStatePayload) {
      state.auth0User = payload.auth0User;
      state.profileData = new ProfileModel();
      state.profileData.userEmail = payload.userEmail;
      state.profileData.userName = payload.userName;
      state.token = `Bearer ${payload.accessToken}`;
      state.isAuthenticated = true;
      state.is401 = false;
      state.userVaultnPermissions = payload.permissions;
      state.allVaultnPermissions = [];
      state.roleGuid = "";
      state.roleName = "";
      state.userGuid = "";
      state.ownerGuid = "";
      state.ownerName = "";
      state.ownerRole = "";
      state.userId = "";
      state.ownerId = "";

      sessionStorage.setItem(authenticatedKey, state.isAuthenticated.toString());
    },

    toggleDrawer(state) {
      state.drawer.open = !state.drawer.open;
    },
    authenticate(state) {
      state.isAuthenticated = true;
      state.is401 = false;
      sessionStorage.setItem(
        authenticatedKey,
        state.isAuthenticated.toString(),
      );
      router.push("/home/dashboard/");
    },
    setIsReportReady(state, val) {
      state.isReportReady = val;
    },
    setIs401(state, val) {
      state.is401 = val;
    },
  },
  actions: {
    logIn(context) {
      context.commit("logIn");
    },
    logOut(context) {
      context.commit("logOut");
    },
    toggleDrawer(context) {
      context.commit("toggleDrawer");
    },

    async setProfileInfo(context): Promise<string | null> {
      context.state.profileLoading = true;
      const [error, response] = await GET_PROFILE_INFO();
      if (error) {
        const errorMessage: any = returnErrors(error);
        notifyError(false, errorMessage, "negative", "report_problem");
        context.state.profileLoading = false;
        return null;
      } else {
        context.state.roleGuid = response.roleGuid;
        context.state.roleName = response.roleName;
        context.state.userGuid = response.userGuid;
        context.state.ownerGuid = response.ownerGuid;
        context.state.ownerName = response.ownerName;
        context.state.ownerRole = response.ownerRole;
        context.state.userId = response.userId;
        context.state.ownerId = response.ownerId;
        context.state.profileLoading = false;
        return response.userId;
      }
    },
    authenticate(context) {
      context.commit("authenticate");
    },
    setIsReportReady(context) {
      context.commit("setIsReportReady");
    },
    setIs401(context) {
      context.commit("setIs401");
    },
  },
  getters: {},
  modules: {},
});

// @ts-ignore
